import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table as MUITable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  InputAdornment,
  Divider,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { MyTableProps, SortDirection, SortConfig, ContextMenuAction } from './types';
import TableContextMenu from './ContextMenu';
import EditModal from './EditModal';
import {
  TableWrapper,
  TableTitle,
  StyledTableContainer,
  StyledTable,
  StyledTableCell,
  StyledTableRow,
  ShowAllButton,
  PaginationButton,
  ActionIconButton,
  SortMenu,
  SortMenuItem,
  StyledTooltip,
  calculateTextWidth,
  debounce,
  truncateText,
} from './Table.styles';

/**
 * Table component with auto-sizing columns and lazy loading for large datasets
 *
 * This component automatically calculates the optimal width for each column based on its content,
 * and then distributes any remaining space proportionally to maximize table space usage.
 * It measures both header and cell content widths and uses the maximum width for each column.
 *
 * Features:
 * - Auto-sizing columns based on content
 * - Proportional distribution of unused space across columns to fill available width
 * - Dynamic resizing on window resize or content changes
 * - Intelligent header truncation - shows full column names when space permits
 * - Minimum width of 60px for each column (40px on mobile)
 * - Maximum width of 250px to prevent extremely wide columns (150px on mobile)
 * - Special handling for the 'todo' column with star icon
 * - Fixed width for the actions column
 * - Responsive design for mobile devices
 * - Lazy loading for large datasets (100+ rows) through infinite scrolling
 *
 * Algorithm for column sizing:
 * 1. Initial calculation based on content dimensions
 * 2. Measurement of available container space
 * 3. Proportional distribution of any extra space
 * 4. Respects min/max constraints for column widths
 * 5. Determines which headers need truncation based on available space
 *
 * You can still provide manual width in the column definition, which will be used as a fallback
 * if the auto-sizing calculation fails or if there's no data to calculate from.
 *
 * To disable auto-sizing and use manual widths exclusively, set the disableAutoSizing prop to true.
 */
const Table = <T extends object>({
  data = [],
  columns = [],
  page = 0,
  onPageChange,
  moduleName,
  totalCount,
  pageSize: controlledPageSize,
  onPageSizeChange,
  rowEventHandlers,
  isRowClickable = false,
  rowClassName,
  onSort,
  defaultSort,
  defaultSorts,
  initialSearchColumn,
  initialSearchTerm,
  defaultFilters,
  disableAutoSizing = false,
  enableSearch = true,
  viewMode = false,
  editMode = false,
  onCellEdit,
  onRowSave,
  onRowCancel,
  readOnlyColumns = [],
  rowBackgroundColor,
  lastRowBold = false,
  customActions,
  customActionButtons,
  hideDefaultActions = false,
  isRowEditable,
  excludedColumns,
  requiredFields,
  textAreaColumns,
  dateColumns,
  priorityReadOnlyColumns,
  defaultReadOnlyColumns,
  todoColumn,
  showPagination = true,
  // Lazy loading properties
  lazyLoad = false,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  lazyLoadHeight = 600, // New prop: configurable height for lazy load container
  lazyLoadingBatchSize = 25, // New prop: how many items to load at once
  maxWidth = '100%',
  // End of lazy loading properties
  tableType,
  ...props
}: MyTableProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pageSize, setPageSize] = useState(controlledPageSize || 5);
  const [currentPage, setCurrentPage] = useState(page);
  const [pageSizeAnchorEl, setPageSizeAnchorEl] = useState<null | HTMLElement>(null);
  const [pageJumpAnchorEl, setPageJumpAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [currentSort, setCurrentSort] = useState<SortConfig | undefined>(defaultSort);
  // Track multiple sorts for display purposes
  const [currentSorts, setCurrentSorts] = useState<SortConfig[]>(defaultSorts || (defaultSort ? [defaultSort] : []));

  // Sync currentSorts with defaultSorts prop when it changes
  useEffect(() => {
    if (defaultSorts && defaultSorts.length > 0) {
      setCurrentSorts(defaultSorts);
      // Also update currentSort to the first sort for backward compatibility
      setCurrentSort(defaultSorts[0]);
    } else if (defaultSort) {
      setCurrentSorts([defaultSort]);
      setCurrentSort(defaultSort);
    } else {
      setCurrentSorts([]);
      setCurrentSort(undefined);
    }
  }, [defaultSorts, defaultSort]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [currentSearchColumn, setCurrentSearchColumn] = useState<string | null>(null);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');
  // Track multiple filters for display purposes
  const [currentFilters, setCurrentFilters] = useState<Array<{ column: string; term: string }>>(defaultFilters || []);

  // Sync currentFilters with defaultFilters prop when it changes
  useEffect(() => {
    if (defaultFilters && defaultFilters.length > 0) {
      setCurrentFilters(defaultFilters);
      // Also update currentSearchColumn/currentSearchTerm for backward compatibility
      if (defaultFilters.length > 0) {
        setCurrentSearchColumn(defaultFilters[0].column);
        setCurrentSearchTerm(defaultFilters[0].term);
      }
    } else {
      setCurrentFilters([]);
      setCurrentSearchColumn(null);
      setCurrentSearchTerm('');
    }
  }, [defaultFilters]);
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [shouldTruncateHeaders, setShouldTruncateHeaders] = useState<Record<string, boolean>>({});
  const tableRef = useRef<HTMLTableElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(true);

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    row: T | null;
  } | null>(null);

  // Add notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'error' | 'warning' | 'info' | 'success';
  } | null>(null);

  // Combine all loading states for a unified experience
  const isTableLoading = isLoading || isScrollLoading || isCalculating;

  // Determine the correct terminology based on module
  const getViewActionText = (): string => {
    // Modules that use "Despatches" - check current path or module name
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Check if we're in e55 or e09 modules based on URL path
    if (currentPath.includes('/e55/') || currentPath.includes('/e09/')) {
      return 'View Despatches';
    }
    
    // Also check moduleName for additional context
    if (moduleName) {
      const lowerModuleName = moduleName.toLowerCase();
      if (lowerModuleName.includes('e55') || lowerModuleName.includes('e09')) {
        return 'View Despatches';
      }
    }
    
    // Default to "View Samples" for eNO1, e53, and other modules
    return 'View Samples';
  };

  const pageSizeOptions = [5, 10, 25, 50, 100];
  const totalPages = Math.ceil((totalCount || data.length) / pageSize);

  // For server-side pagination, we don't need to slice the data as it should already be paginated
  // But we keep the displayedData calculation for client-side fallback
  const displayedData = useMemo(() => {
    // If lazy loading is enabled, use the full data array
    if (lazyLoad) {
      return data;
    }
    
    // If totalCount is provided, we assume server-side pagination is being used
    // and the data is already paginated
    if (totalCount !== undefined) {
      return data;
    }

    // Otherwise, use client-side pagination
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize, totalCount, lazyLoad]);

  // Calculate actual chunk size based on data size to optimize for large datasets
  const effectiveChunkSize = useMemo(() => {
    // For very large datasets, use larger chunks to improve performance
    if (data.length > 1000) return Math.min(Math.max(50, lazyLoadingBatchSize), 100);
    if (data.length > 500) return Math.min(Math.max(35, lazyLoadingBatchSize), 50);
    return lazyLoadingBatchSize;
  }, [data.length, lazyLoadingBatchSize]);

  // Memoize the column width calculation function
  const calculateColumnWidths = useCallback(() => {
    // Skip calculation if auto-sizing is disabled
    if (disableAutoSizing || data.length === 0 || columns.length === 0) {
      setIsCalculating(false);
      return;
    }

    const newColumnWidths: Record<string, number> = {};
    const newShouldTruncateHeaders: Record<string, boolean> = {};
    const headerFont = '700 12px "Lato", sans-serif';
    const cellFont = '400 12px "Lato", sans-serif';

    // Define min and max column widths
    const minColWidth = isMobile ? 40 : 60;
    const maxColWidth = isMobile ? 150 : 320;

    // Performance optimization for large datasets:
    // Sample a limited number of rows instead of processing all rows
    const MAX_ROWS_TO_SAMPLE = 20; // Only analyze a maximum of 20 rows for width calculation
    const rowsToSample = data.length > MAX_ROWS_TO_SAMPLE 
      ? [
          // Include some rows from the beginning
          ...data.slice(0, Math.min(10, data.length)), 
          // Include some rows from the middle if there are many rows
          ...(data.length > 20 ? data.slice(Math.floor(data.length / 2), Math.floor(data.length / 2) + 5) : []),
          // Include some rows from the end
          ...(data.length > 10 ? data.slice(Math.max(0, data.length - 5), data.length) : [])
        ] 
      : data;
      
    // Step 1: Calculate initial content-based widths for each column
    columns.forEach((column: any) => {
      const accessor = column.accessor;

      // Calculate full header width (including padding for icon)
      const fullHeaderWidth = calculateTextWidth(column.Header, headerFont) + 40;

      // Start with header width as max width
      let maxWidth = fullHeaderWidth;

      // Check sampled cell values in this column
      rowsToSample.forEach((row: any) => {
        let cellContent = row[accessor];

        // Handle special case for todo column with star icon
        if (accessor === 'todo') {
          maxWidth = Math.max(
            maxWidth,
            calculateTextWidth(String(cellContent || ''), cellFont) + 40
          );
        } else if (cellContent !== undefined && cellContent !== null) {
          // For complex content (objects, arrays), use a reasonable default width
          if (typeof cellContent === 'object') {
            // If the column has a custom Cell renderer, assume it needs more space
            if (column.Cell) {
              maxWidth = Math.max(maxWidth, 120);
            } else {
              // For objects without custom renderers, use JSON string length as estimate
              try {
                const jsonString = JSON.stringify(cellContent);
                maxWidth = Math.max(
                  maxWidth,
                  calculateTextWidth(jsonString.substring(0, 50), cellFont) + 24
                );
              } catch (e) {
                maxWidth = Math.max(maxWidth, 80);
              }
            }
          } else {
            // For simple content (strings, numbers), measure the text
            maxWidth = Math.max(maxWidth, calculateTextWidth(String(cellContent), cellFont) + 24);
          }
        }
      });

      // Apply min/max constraints
      maxWidth = Math.max(maxWidth, minColWidth);
      maxWidth = Math.min(maxWidth, maxColWidth);

      // Determine if header needs truncation
      // For mobile: if header width is greater than max allowed, truncate
      // For desktop: if header width is much greater than content width, truncate
      newShouldTruncateHeaders[accessor] = isMobile
        ? fullHeaderWidth > maxColWidth
        : fullHeaderWidth > maxWidth * 1.2; // Allow 20% extra room before truncating

      // Store the calculated width
      newColumnWidths[accessor] = maxWidth;
    });

    // Add action column width - different width depending on edit mode
    const actionColumnWidth = editMode
      ? isMobile
        ? 50
        : 60 // Smaller width in edit mode (one button)
      : isMobile
        ? 100
        : 120; // Larger width in normal mode (multiple buttons)

    newColumnWidths['actions'] = actionColumnWidth;
    newShouldTruncateHeaders['actions'] = true; // Always truncate the actions column header

    // Step 2: Determine available space and proportionally adjust columns
    if (tableContainerRef.current) {
      // Get available width (container width minus padding/margins/scrollbar)
      const containerWidth = tableContainerRef.current.clientWidth - 20; // Account for scrollbar and padding

      // Calculate total width of all columns based on initial sizing
      const totalColumnsWidth = Object.values(newColumnWidths).reduce(
        (sum, width) => sum + width,
        0
      );

      // If we have unused space and the total isn't already filling the container
      if (containerWidth > totalColumnsWidth) {
        const extraSpace = containerWidth - totalColumnsWidth;

        // Calculate the total width of columns that can be expanded (excluding action column)
        const expandableColumnsWidth = totalColumnsWidth - actionColumnWidth;

        // Distribute extra space proportionally to each column based on its current width percentage
        columns.forEach((column: any) => {
          const accessor = column.accessor;
          if (accessor !== 'actions') {
            // Calculate how much this column should grow based on its proportion of the total expandable width
            const columnProportion = newColumnWidths[accessor] / expandableColumnsWidth;
            const extraWidthForColumn = Math.floor(extraSpace * columnProportion);

            // Add the extra width to the column, respecting the max width constraint
            const newWidth = Math.min(newColumnWidths[accessor] + extraWidthForColumn, maxColWidth);

            // Re-evaluate if we need to truncate headers with the new widths
            const fullHeaderWidth = calculateTextWidth(column.Header, headerFont) + 40;
            // If the new width can accommodate the full header, don't truncate
            if (fullHeaderWidth <= newWidth) {
              newShouldTruncateHeaders[accessor] = false;
            }

            newColumnWidths[accessor] = newWidth;
          }
        });
      }
    }

    // Convert numeric widths to strings with 'px' suffix
    const finalColumnWidths: Record<string, string> = {};
    for (const [key, value] of Object.entries(newColumnWidths)) {
      finalColumnWidths[key] = `${value}px`;
    }

    setColumnWidths(finalColumnWidths);
    setShouldTruncateHeaders(newShouldTruncateHeaders);
    setIsCalculating(false); // Mark calculation as complete
  }, [columns, data, disableAutoSizing, isMobile, editMode]);

  // Debounced version of the calculation function
  const debouncedCalculateColumnWidths = useMemo(
    () => debounce(calculateColumnWidths, 300),
    [calculateColumnWidths]
  );

  // Calculate optimal column widths based on content
  useEffect(() => {
    // Skip on initial mount to prevent double calculation
    if (isInitialMount.current) {
      isInitialMount.current = false;
      setIsCalculating(true);
      calculateColumnWidths();
      return;
    }

    // Use debounced function for subsequent updates
    setIsCalculating(true);
    debouncedCalculateColumnWidths();
  }, [calculateColumnWidths, debouncedCalculateColumnWidths]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      debouncedCalculateColumnWidths();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [debouncedCalculateColumnWidths]);

  // Implement infinite scroll for lazy loading
  useEffect(() => {
    if (!lazyLoad || !tableContainerRef.current || isTableLoading) return;

    // Only enable infinite scroll after column widths have been calculated
    // This prevents scrolling issues during initial layout
    if (Object.keys(columnWidths).length === 0) return;

    const handleScroll = debounce(() => {
      if (!tableContainerRef.current || isTableLoading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
      
      // Load more data when user has scrolled to the bottom (with a 50px threshold)
      if (scrollHeight - scrollTop - clientHeight < 50 && onLoadMore && hasMore) {
        setIsScrollLoading(true);
        onLoadMore()
          .then(() => {
            // Recalculate column widths after loading more data
            // Use a timeout to ensure the DOM has updated with new rows
            setTimeout(() => {
              setIsCalculating(true);
              calculateColumnWidths();
            }, 100);
          })
          .catch((error) => {
            console.error('Error loading more data:', error);
            setNotification({
              open: true,
              message: t('table.loadError', 'Failed to load more data. Please try again.'),
              type: 'error',
            });
          })
          .finally(() => {
            setIsScrollLoading(false);
          });
      }
    }, 200);

    const tableContainer = tableContainerRef.current;
    tableContainer.addEventListener('scroll', handleScroll);

    return () => {
      tableContainer.removeEventListener('scroll', handleScroll);
    };
  }, [lazyLoad, onLoadMore, hasMore, columnWidths, calculateColumnWidths, t, isTableLoading]);

  // Reset calculation state when data changes (new data loaded)
  useEffect(() => {
    if (data.length > 0 && !isInitialMount.current) {
      setIsCalculating(true);
      // Use a small delay to ensure the DOM has updated
      setTimeout(() => {
        calculateColumnWidths();
      }, 50);
    }
  }, [data, calculateColumnWidths]);

  // Initialize search state from URL parameters or external state
  useEffect(() => {
    // Initialize search state from props
    if (initialSearchColumn && initialSearchTerm) {
      setCurrentSearchColumn(initialSearchColumn);
      setCurrentSearchTerm(initialSearchTerm);
      setIsSearchActive(true);
      // Set the active column to the search column if it's not already set
      if (!activeColumn) {
        setActiveColumn(initialSearchColumn);
      }
    }
  }, [initialSearchColumn, initialSearchTerm, activeColumn]);

  const handlePageSizeChange = (newSize: number) => {
    // Update local state
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page when changing page size

    // Call the external handler to update query params and refetch data
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }

    setPageSizeAnchorEl(null);
  };

  const handlePageJump = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      // Update local state
      setCurrentPage(newPage);

      // Call the external handler to update query params and refetch data
      if (onPageChange) {
        onPageChange(newPage);
      }
    }
    setPageJumpAnchorEl(null);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      // Update local state
      setCurrentPage(newPage);

      // Call the external handler to update query params and refetch data
      if (onPageChange) {
        onPageChange(newPage);
      }
    }
  };

  const handleRowClick = (row: T, event: React.MouseEvent) => {
    if (rowEventHandlers?.onClick) {
      rowEventHandlers.onClick(row, event);
    }
  };

  const handleRowDoubleClick = (row: T, event: React.MouseEvent) => {
    // In edit mode, we'll use context menu instead of double-click
    if (editMode) {
      // Just call the external handler if provided, but don't open edit modal
      if (rowEventHandlers?.onDoubleClick) {
        rowEventHandlers.onDoubleClick(row, event);
      }
    } else if (rowEventHandlers?.onDoubleClick) {
      rowEventHandlers.onDoubleClick(row, event);
    }
  };

  const handleRowContextMenu = (row: T, event: React.MouseEvent) => {
    // Only disable context menu if in view mode AND there are no custom actions
    // This allows custom contextMenuActions to show in view mode
    if (viewMode && (!customActions || customActions.length === 0)) return;

    event.preventDefault(); // Prevent default context menu

    // Set context menu position and active row
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      row: row,
    });

    // Also call the external context menu handler if provided
    if (rowEventHandlers?.onContextMenu) {
      rowEventHandlers.onContextMenu(row, event);
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleContextMenuAction = (action: ContextMenuAction) => {
    if (!contextMenu?.row) return;

    // If there's an external handler, let it handle the action first (including 'edit')
    // This allows custom edit modals to take precedence over the internal one
    if (rowEventHandlers?.onContextMenuAction) {
      rowEventHandlers.onContextMenuAction(action, contextMenu.row, tableType);
      // If it's an edit action and we have an external handler, don't use internal modal
      if (action === 'edit') {
        handleCloseContextMenu();
        return;
      }
    }

    // In edit mode, handle edit action with internal modal only if no external handler
    if (editMode && action === 'edit' && 'id' in contextMenu.row && !rowEventHandlers?.onContextMenuAction) {
      setIsEditModalOpen(true);
      setSelectedRow(contextMenu.row);
      handleCloseContextMenu();
      return;
    }
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setActiveColumn(column);
    
    if (enableSearch) {
      // Check if this column has a filter in currentFilters
      const columnFilter = currentFilters.find(f => f.column === column);
      
      if (columnFilter) {
        // If column has a filter, show the filter term in the input
        setSearchTerm(columnFilter.term);
        setIsSearchActive(true);
        setCurrentSearchColumn(column);
        setCurrentSearchTerm(columnFilter.term);
      } else if (currentSearchColumn === column && currentSearchTerm) {
        // If switching to a column that has an active search, show that search term
        setSearchTerm(currentSearchTerm);
        setIsSearchActive(true);
      } else if (currentSearchColumn !== column) {
        // If switching to a different column without a filter, clear the search input
        setSearchTerm('');
        setIsSearchActive(false);
      }
    }
    
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortSelect = (direction: SortDirection) => {
    if (activeColumn && onSort) {
      // Check if we're clicking the same sort direction - if so, disable sorting
      if (currentSort && currentSort.column === activeColumn && currentSort.direction === direction) {
        // Disable sorting by calling onSort with undefined direction
        setCurrentSort(undefined);
        if (searchTerm.trim()) {
          setIsSearchActive(true);
          setCurrentSearchColumn(activeColumn);
          setCurrentSearchTerm(searchTerm.trim());
          onSort(activeColumn, undefined as any, activeColumn, searchTerm.trim());
        } else {
          setIsSearchActive(false);
          setCurrentSearchColumn(null);
          setCurrentSearchTerm('');
          onSort(activeColumn, undefined as any);
        }
      } else {
        // Apply new sorting
        setCurrentSort({ column: activeColumn, direction });
        if (searchTerm.trim()) {
          setIsSearchActive(true);
          setCurrentSearchColumn(activeColumn);
          setCurrentSearchTerm(searchTerm.trim());
          onSort(activeColumn, direction, activeColumn, searchTerm.trim());
        } else {
          setIsSearchActive(false);
          setCurrentSearchColumn(null);
          setCurrentSearchTerm('');
          onSort(activeColumn, direction);
        }
      }
    }
    setSortAnchorEl(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!enableSearch) return;
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (direction: SortDirection) => {
    if (!enableSearch) return;
    if (activeColumn && onSort && searchTerm.trim()) {
      // For search-only, don't set sort config, just call onSort with search params only
      setIsSearchActive(true);
      onSort(activeColumn, direction, activeColumn, searchTerm.trim());
      setSortAnchorEl(null);
    }
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!enableSearch) return;
    // Stop propagation to prevent menu keyboard navigation
    event.stopPropagation();
    if (event.key === 'Enter' && searchTerm.trim()) {
      // When searching via Enter, don't apply sorting - just search
      if (activeColumn && onSort) {
        setIsSearchActive(true);
        setCurrentSearchColumn(activeColumn);
        setCurrentSearchTerm(searchTerm.trim());
        // Pass undefined for sort direction to indicate search-only
        onSort(activeColumn, undefined as any, activeColumn, searchTerm.trim());
        setSortAnchorEl(null);
      }
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Stop propagation to prevent menu keyboard navigation from intercepting key presses
    event.stopPropagation();
  };

  const handleClearSearch = () => {
    if (!enableSearch) return;
    setSearchTerm('');
    setIsSearchActive(false);
    setCurrentSearchColumn(null);
    setCurrentSearchTerm('');
    
    // Update local filters state to remove the filter for activeColumn
    if (activeColumn) {
      const newFilters = currentFilters.filter(f => f.column !== activeColumn);
      setCurrentFilters(newFilters);
    }
    
    if (activeColumn && onSort) {
      // Clear filter for this column by passing empty searchTerm
      // Pass activeColumn as searchColumn and empty string as searchTerm to remove the filter
      const currentDirection = currentSort?.direction || undefined;
      onSort(activeColumn, currentDirection, activeColumn, '');
    }
    setSortAnchorEl(null);
  };

  const handleShowAll = () => {
    const totalItems = totalCount || data.length;
    // Update local state
    setPageSize(totalItems);
    setCurrentPage(0);

    // Call the external handler to update query params and refetch data
    if (onPageSizeChange) {
      onPageSizeChange(totalItems);
    }
  };

  // Handle cell value change in edit mode
  const handleCellChange = (column: string, value: any) => {
    if (!selectedRow) return;

    // Create a new copy of the edited row with the updated value
    const updatedRow = {
      ...selectedRow,
      [column]: value,
    };

    setSelectedRow(updatedRow);

    // Call the external cell edit handler if provided
    if (onCellEdit) {
      onCellEdit(updatedRow, column, value);
    }
  };

  // Handle saving edited row
  const handleSaveRow = async (editedRow: T, originalRow: T) => {
    if (!onRowSave) return false;

    try {
      const result = await Promise.resolve(onRowSave(editedRow, originalRow));

      if (result) {
        // If save was successful, exit edit mode
        setIsEditModalOpen(false);
        setSelectedRow(null);
      }

      return result;
    } catch (error) {
      console.error('Error saving row:', error);
      return false;
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setSelectedRow(null);

    if (onRowCancel && selectedRow) {
      onRowCancel(selectedRow);
    }
  };

  // If editing is active and the row is no longer in the data (e.g., after pagination), cancel editing
  useEffect(() => {
    if (isEditModalOpen && selectedRow && 'id' in selectedRow) {
      const rowStillExists = displayedData.some(
        (row: any) => 'id' in row && row.id === (selectedRow as any).id
      );
      if (!rowStillExists) {
        handleCancelEdit();
      }
    }
  }, [displayedData, isEditModalOpen, selectedRow]);

  // Cancel editing on ESC key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditModalOpen) {
        handleCancelEdit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditModalOpen]);

  // Cell renderer that handles both normal and edit modes
  const renderCell = (row: any, column: any, rowIndex: number) => {
    const accessor = column.accessor;
    const isOtherRowEditing = isEditModalOpen;
    const isLastRow = rowIndex === displayedData.length - 1;

    // Handle special case for todo column with star icon
    if (accessor === 'todo') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            opacity: isOtherRowEditing ? 0.5 : 1,
          }}
          data-testid={`cell-${accessor}-${row.id || rowIndex}`}
        >
          {row.status === 'F' ? (
            <StarIcon
              sx={{
                fontSize: isMobile ? 14 : 16,
                minWidth: isMobile ? '14px' : '16px',
                color: 'inherit',
              }}
              data-testid={`star-icon-filled-${row.id || rowIndex}`}
            />
          ) : (
            <StarBorderIcon
              sx={{
                fontSize: isMobile ? 14 : 16,
                minWidth: isMobile ? '14px' : '16px',
                color: 'inherit',
              }}
              data-testid={`star-icon-border-${row.id || rowIndex}`}
            />
          )}
        </Box>
      );
    }

    // For non-editing mode or read-only columns - we no longer need the inline editing part
    return (
      <Typography
        component='div'
        sx={{
          opacity: isOtherRowEditing ? 0.5 : 1,
          fontSize: isMobile ? '11px' : '12px',
          textAlign: column.textAlign || 'center',
          // Apply bold styling to Typography component for last row
          fontWeight: lastRowBold && isLastRow ? '800' : undefined,
        }}
        data-testid={`cell-${accessor}-${row.id || rowIndex}`}
      >
        {column.Cell ? column.Cell({ value: row[accessor], row: row }) : row[accessor]}
      </Typography>
    );
  };

  // Add notification close handler
  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <TableWrapper data-testid="table-wrapper" maxWidth={maxWidth}>
      <Box>
        <TableTitle data-testid="table-title">
          {moduleName ? `${moduleName}` : t('table.pendingActions', '')}
        </TableTitle>
      </Box>

      {/* Universal loading indicator for all table loading states */}
      {isTableLoading && (
        <Box sx={{ 
          width: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 4 
        }}
        data-testid="table-loading-indicator">
          <CircularProgress size={36} color="primary" />
        </Box>
      )}

      <StyledTableContainer
        ref={tableContainerRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxWidth: '100%',
          width: '100%',
          display: isTableLoading ? 'none' : 'block',
          '& .MuiTable-root': {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}
        data-testid="table-container"
      >
        <StyledTable
          ref={tableRef}
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'auto',
            minWidth: '100%',
          }}
          data-testid="data-table"
        >
          <TableHead data-testid="table-head">
            <StyledTableRow data-testid="table-header-row">
              {columns.map((column: any) => (
                <StyledTableCell
                  key={column.accessor}
                  width={
                    !disableAutoSizing
                      ? columnWidths[column.accessor] || column.width || 'auto'
                      : column.width || 'auto'
                  }
                  data-text-align={column.textAlign}
                  sx={{
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}
                  data-testid={`header-cell-${column.accessor}`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      position: 'relative',
                      pr: isMobile ? 0.5 : 1,
                    }}
                    data-testid={`header-box-${column.accessor}`}
                  >
                    <StyledTooltip title={column.Header} placement='top' arrow>
                      <Typography
                        sx={{
                          fontSize: isMobile ? '10px' : '12px',
                          fontWeight: 700,
                          width: '100%',
                          textAlign: 'center',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                        data-testid={`header-text-${column.accessor}`}
                      >
                        {shouldTruncateHeaders[column.accessor]
                          ? truncateText(column.Header, isMobile ? 3 : 4)
                          : column.Header}
                      </Typography>
                    </StyledTooltip>
                    <IconButton
                      size='small'
                      onClick={(e) => handleSortClick(e, column.accessor)}
                      sx={{
                        position: 'absolute',
                        right: -4,
                        top: '50%',
                        backgroundColor: currentFilters.some(f => f.column === column.accessor) || currentSorts.some(s => s.column === column.accessor)
                          ? '#E3F2FD !important' 
                          : '#E7EFF5 !important',
                        transform: 'translateY(-50%)',
                        padding: isMobile ? '1px' : '2px',
                        border: currentFilters.some(f => f.column === column.accessor) || currentSorts.some(s => s.column === column.accessor)
                          ? '1px solid #2196F3' 
                          : 'none',
                        '&:hover': {
                          backgroundColor: currentSorts.some(s => s.column === column.accessor) || currentFilters.some(f => f.column === column.accessor)
                            ? '#FFEBEE !important' // Red tint to indicate can be disabled
                            : '#E3F2FD !important',
                        },
                      }}
                      data-testid={`sort-button-${column.accessor}`}
                    >
                      {(() => {
                        // Check if this column has an active sort in currentSorts
                        const columnSort = currentSorts.find(s => s.column === column.accessor);
                        if (columnSort) {
                          return columnSort.direction === 'ASC' ? (
                            <ArrowUpwardIcon 
                              sx={{ 
                                fontSize: isMobile ? 10 : 12,
                                color: currentFilters.some(f => f.column === column.accessor) ? '#2196F3' : '#1976D2'
                              }} 
                              data-testid={`sort-asc-icon-${column.accessor}`} 
                            />
                          ) : (
                            <ArrowDownwardIcon 
                              sx={{ 
                                fontSize: isMobile ? 10 : 12,
                                color: currentFilters.some(f => f.column === column.accessor) ? '#2196F3' : '#1976D2'
                              }} 
                              data-testid={`sort-desc-icon-${column.accessor}`} 
                            />
                          );
                        }
                        // Show filter indicator if column has a filter but no sort
                        const hasFilter = currentFilters.some(f => f.column === column.accessor);
                        return (
                          <MoreVertIcon 
                            sx={{ 
                              fontSize: isMobile ? 10 : 12,
                              color: hasFilter ? '#2196F3' : 'inherit'
                            }} 
                            data-testid={`sort-more-icon-${column.accessor}`} 
                          />
                        );
                      })()}
                    </IconButton>
                  </Box>
                </StyledTableCell>
              ))}
              {(!viewMode || editMode) && (
                <StyledTableCell
                  className='actions-cell'
                  sx={{
                    background: '#E7EFF5 !important',
                    zIndex: 3,
                    width: editMode
                      ? isMobile
                        ? '80px !important'
                        : '100px !important' // Increased width in edit mode
                      : isMobile
                        ? '160px !important'
                        : '160px !important', // Increased width in regular mode
                    maxWidth: editMode
                      ? isMobile
                        ? '80px !important'
                        : '100px !important' // Increased width in edit mode
                      : isMobile
                        ? '160px !important'
                        : '1600px !important', // Increased width in regular mode
                    minWidth: editMode
                      ? isMobile
                        ? '80px !important'
                        : '100px !important' // Increased width in edit mode
                      : isMobile
                        ? '160px !important'
                        : '160px !important', // Increased width in regular mode
                    padding: editMode ? '0 8px !important' : '0 12px !important', // Increased padding
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      textAlign: 'center',
                      pr: isMobile ? 0.5 : 1,
                    }}
                  >
                    <StyledTooltip title={t('table.actions', 'Actions')} placement='top' arrow>
                      <Typography
                        sx={{
                          fontSize: isMobile ? '10px' : '12px',
                          fontWeight: 700,
                          width: '100%',
                          textAlign: 'center',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {shouldTruncateHeaders['actions']
                          ? truncateText(t('table.actions', 'Actions'), isMobile ? 3 : 4)
                          : t('table.actions', 'Actions')}
                      </Typography>
                    </StyledTooltip>
                    <MoreVertIcon
                      sx={{
                        fontSize: isMobile ? 10 : 12,
                        position: 'absolute',
                        right: -2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    />
                  </Box>
                </StyledTableCell>
              )}

              {/* Add actions header for view mode when custom actions are available */}
              {viewMode && !editMode && customActions && customActions.length > 0 && (
                <StyledTableCell
                  className='actions-cell'
                  sx={{
                    background: '#E7EFF5 !important',
                    zIndex: 3,
                    width: isMobile ? '50px !important' : '60px !important',
                    maxWidth: isMobile ? '50px !important' : '60px !important',
                    minWidth: isMobile ? '50px !important' : '60px !important',
                    padding: '0 4px !important',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      textAlign: 'center',
                      pr: isMobile ? 0.5 : 1,
                    }}
                  >
                    <StyledTooltip title={t('table.more', 'More')} placement='top' arrow>
                      <Typography
                        sx={{
                          fontSize: isMobile ? '10px' : '12px',
                          fontWeight: 700,
                          width: '100%',
                          textAlign: 'center',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {shouldTruncateHeaders['actions']
                          ? truncateText(t('table.more', 'More'), isMobile ? 3 : 4)
                          : t('table.more', 'More')}
                      </Typography>
                    </StyledTooltip>
                    <MoreVertIcon
                      sx={{
                        fontSize: isMobile ? 10 : 12,
                        position: 'absolute',
                        right: -2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    />
                  </Box>
                </StyledTableCell>
              )}
            </StyledTableRow>
          </TableHead>
          <TableBody
            sx={{
              '& .alternate': {
                backgroundColor: rowBackgroundColor ? 'inherit' : '#f5f5f5',
              },
              '& .status-F': {
                backgroundColor: rowBackgroundColor ? 'inherit' : 'rgba(255, 248, 225, 0.5)',
              },
              '& .status-D': {
                backgroundColor: rowBackgroundColor ? 'inherit' : 'rgba(232, 245, 233, 0.5)',
              },
              '& .disabled-row': {
                opacity: 0.5,
                pointerEvents: 'none',
                cursor: 'not-allowed',
                filter: 'grayscale(0.5)',
              },
            }}
          >
            {displayedData.map((row: any, index: number) => {
              const isLastRow = index === displayedData.length - 1;
              return (
                <StyledTableRow
                  key={row.id || index}
                  className={`status-${row.status} ${index % 2 === 1 ? 'alternate' : ''} ${rowClassName?.(row) || ''}`}
                  $clickable={(isRowClickable || editMode) && !isEditModalOpen}
                  onClick={(e) => (!isEditModalOpen ? handleRowClick(row, e) : undefined)}
                  onDoubleClick={(e) =>
                    !isEditModalOpen ? handleRowDoubleClick(row, e) : undefined
                  }
                  onContextMenu={(e) => {
                    // Allow context menu in view mode if there are custom actions
                    const allowContextMenu =
                      !isEditModalOpen &&
                      (viewMode ? !!(customActions && customActions.length > 0) : true);
                    if (allowContextMenu) {
                      handleRowContextMenu(row, e);
                    }
                  }}
                  data-testid={`table-row-${row.id || index}`}
                  sx={{
                    '&:hover': {
                      background:
                        (isRowClickable ||
                          editMode ||
                          (viewMode && customActions && customActions.length > 0)) &&
                        !isEditModalOpen
                          ? rowBackgroundColor 
                            ? typeof rowBackgroundColor === 'function'
                              ? `${rowBackgroundColor(row)}e0` // Add some transparency on hover
                              : `${rowBackgroundColor}e0` 
                            : '#f0f0f0'
                          : undefined,
                    },
                    '& .actions-cell': {
                      background: '#E7EFF5 !important',
                    },
                    cursor:
                      editMode && !isEditModalOpen
                        ? 'context-menu'
                        : viewMode && customActions && customActions.length > 0 && !isEditModalOpen
                          ? 'context-menu'
                          : isRowClickable && !isEditModalOpen
                            ? 'pointer'
                            : columns.length > 6
                              ? 'grab'
                              : 'default', // Use grab cursor to indicate scrollable content
                    '&:active': {
                      cursor: columns.length > 6 ? 'grabbing' : undefined, // Change to grabbing when clicked/dragged
                    },
                    // Apply rowBackgroundColor if provided with higher priority
                    backgroundColor: rowBackgroundColor 
                      ? typeof rowBackgroundColor === 'function' 
                        ? `${rowBackgroundColor(row)} !important` 
                        : `${rowBackgroundColor} !important`
                      : undefined,
                    // Bold styling is now applied directly to Typography component in renderCell
                  }}
                >
                  {columns.map((column: any) => (
                    <StyledTableCell
                      key={column.accessor}
                      width={
                        !disableAutoSizing
                          ? columnWidths[column.accessor] || column.width || 'auto'
                          : column.width || 'auto'
                      }
                      className={column.accessor === 'todo' ? 'todo-cell' : ''}
                      data-text-align={column.textAlign}
                      sx={{
                        justifyContent: column.textAlign === 'right' ? 'flex-end' : (column.textAlign === 'left' ? 'flex-start' : 'center'),
                        textAlign: column.textAlign || 'center',
                      }}
                      data-testid={`row-cell-${row.id || index}-${column.accessor}`}
                    >
                      {renderCell(row, column, index)}
                    </StyledTableCell>
                  ))}

                  {(!viewMode || editMode) && (
                    <StyledTableCell
                      className='actions-cell'
                      sx={{
                        background: '#E7EFF5 !important',
                        width: editMode
                          ? isMobile
                            ? '80px !important'
                            : '100px !important' // Increased width in edit mode
                          : isMobile
                            ? '160px !important'
                            : '160px !important', // Increased width in regular mode
                        maxWidth: editMode
                          ? isMobile
                            ? '80px !important'
                            : '100px !important' // Increased width in edit mode
                          : isMobile
                            ? '160px !important'
                            : '160px !important', // Increased width in regular mode
                        minWidth: editMode
                          ? isMobile
                            ? '80px !important'
                            : '100px !important' // Increased width in edit mode
                          : isMobile
                            ? '160px !important'
                            : '160px !important', // Increased width in regular mode
                        padding: editMode ? '0 8px !important' : '0 12px !important', // Increased padding
                      }}
                    >
                      {editMode ? (
                        // For edit mode, show edit icon that opens the edit modal when clicked
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            width: '100%',
                            gap: '8px', // Added gap between buttons
                          }}
                        >
                         
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              // Always prefer custom context menu action handler if it exists
                              // This ensures each module (eno1, e09, e53, e55) uses its own edit modal
                              if (rowEventHandlers?.onContextMenuAction) {
                                // Call the custom handler - it will decide what to do
                                rowEventHandlers.onContextMenuAction('editSample', row);
                                return;
                              }
                              
                              // Fallback to built-in modal only if no custom handler exists
                              if (!isEditModalOpen && isRowEditable && isRowEditable(row)) {
                                setSelectedRow(row);
                                setIsEditModalOpen(true);
                              } else if (!isEditModalOpen) {
                                setNotification({
                                  open: true,
                                  message: t('table.notEditable', 'Only valid rows can be edited'),
                                  type: 'error',
                                });
                              }
                            }}
                            sx={{
                              padding: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                            data-testid="edit-row-button"
                          >
                            <EditIcon
                              sx={{
                                fontSize: isMobile ? 14 : 16,
                                color: 'primary.main',
                              }}
                            />
                          </IconButton>
                          {/* Add Delete Amendment button - only shown for status 'D' rows (supports both sampleStatus and despatchStatus) */}
                          {(row?.sampleStatus === 'D' || row?.despatchStatus === 'D') && (
                            <ActionIconButton 
                              size='small' 
                              disabled={isEditModalOpen}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Trigger the deleteAmendment action
                                if (rowEventHandlers?.onContextMenuAction) {
                                  rowEventHandlers.onContextMenuAction('deleteAmendment', row);
                                }
                              }}
                            >
                              <StyledTooltip title={t('table.deleteAmendment', 'Delete Amendment')} placement='top' arrow>
                                <DeleteIcon
                                  sx={{
                                    fontSize: isMobile ? 14 : 16,
                                    color: 'primary.main',
                                  }}
                                />
                              </StyledTooltip>
                            </ActionIconButton>
                          )}
                          
                        </Box>
                      ) : (
                        // Show action buttons when not in edit mode
                        <Box sx={{ 
                          display: 'flex', 
                          gap: isMobile ? '4px' : '8px',  // Increased gap between buttons
                          justifyContent: 'center', // Center the buttons
                          width: '100%' 
                        }}>
                          {customActionButtons && customActionButtons.length > 0 ? (
                            // Render custom action buttons
                            customActionButtons.map((actionButton, buttonIndex) => (
                              <ActionIconButton
                                key={buttonIndex}
                                size='small'
                                disabled={isEditModalOpen || (actionButton.disabled && actionButton.disabled(row))}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  actionButton.onClick(row);
                                }}
                                sx={{ 
                                  padding: isMobile ? '6px' : '8px' // Increased button padding
                                }}
                                data-testid={actionButton.testId || `custom-action-button-${buttonIndex}`}
                              >
                                <StyledTooltip title={actionButton.tooltip} placement='top' arrow>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: isMobile ? 16 : 20,
                                      color: isEditModalOpen || (actionButton.disabled && actionButton.disabled(row))
                                        ? 'rgba(0, 0, 0, 0.26)' 
                                        : 'inherit',
                                    }}
                                  >
                                    {actionButton.icon}
                                  </Box>
                                </StyledTooltip>
                              </ActionIconButton>
                            ))
                          ) : (
                            // Render default action buttons
                            <>
                              <ActionIconButton 
                                size='small' 
                                disabled={isEditModalOpen}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  if (rowEventHandlers?.onContextMenuAction) {
                                    rowEventHandlers.onContextMenuAction('download', row);
                                  }
                                }}
                                sx={{ 
                                  padding: isMobile ? '6px' : '8px' // Increased button padding
                                }}
                                data-testid="download-button"
                              >
                                <StyledTooltip title={t('table.download', 'Download')} placement='top' arrow>
                                  <SaveIcon
                                    sx={{
                                      fontSize: isMobile ? 16 : 20, // Increased icon size
                                      color: isEditModalOpen ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
                                    }}
                                  />
                                </StyledTooltip>
                              </ActionIconButton>

                              {/* View Samples/Despatches Action Button */}
                              <ActionIconButton 
                                size='small' 
                                disabled={isEditModalOpen}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  if (rowEventHandlers?.onContextMenuAction) {
                                    const action = getViewActionText() === 'View Despatches' ? 'ViewDespatches' : 'ViewSamples';
                                    rowEventHandlers.onContextMenuAction(action as any, row);
                                  }
                                }}
                                sx={{ 
                                  padding: isMobile ? '6px' : '8px' // Increased button padding
                                }}
                                data-testid={getViewActionText() === 'View Despatches' ? "view-despatches-button" : "view-samples-button"}
                              >
                                <StyledTooltip title={getViewActionText()} placement='top' arrow>
                                  <VisibilityIcon
                                    sx={{
                                      fontSize: isMobile ? 16 : 20, // Increased icon size
                                      color: isEditModalOpen ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
                                    }}
                                  />
                                </StyledTooltip>
                              </ActionIconButton>
                              
                              <ActionIconButton 
                                size='small' 
                                disabled={isEditModalOpen}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (rowEventHandlers?.onContextMenuAction) {
                                    rowEventHandlers.onContextMenuAction('accept', row);
                                  }
                                }}
                                sx={{ 
                                  padding: isMobile ? '6px' : '8px' // Increased button padding
                                }}
                                data-testid="accept-button"
                              >
                                <StyledTooltip title={t('table.accept', 'Accept')} placement='top' arrow>
                                  <CheckIcon
                                    sx={{
                                      fontSize: isMobile ? 16 : 20, // Increased icon size
                                      color: isEditModalOpen ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
                                    }}
                                  />
                                </StyledTooltip>
                              </ActionIconButton>
                              
                              <ActionIconButton 
                                size='small' 
                                disabled={isEditModalOpen}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (rowEventHandlers?.onContextMenuAction) {
                                    rowEventHandlers.onContextMenuAction('delete', row);
                                  }
                                }}
                                sx={{ 
                                  padding: isMobile ? '6px' : '8px' // Increased button padding
                                }}
                                data-testid="delete-button"
                              >
                                <StyledTooltip title={t('table.delete', 'Delete')} placement='top' arrow>
                                  <CloseIcon
                                    sx={{
                                      fontSize: isMobile ? 16 : 20, // Increased icon size
                                      color: isEditModalOpen ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
                                    }}
                                  />
                                </StyledTooltip>
                              </ActionIconButton>
                              
                              <ActionIconButton
                                size='small'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowContextMenu(row, e as React.MouseEvent);
                                }}
                                disabled={isEditModalOpen}
                                sx={{ 
                                  padding: isMobile ? '6px' : '8px' // Increased button padding
                                }}
                                data-testid="more-actions-button"
                              >
                                <StyledTooltip title={t('table.more', 'More Actions')} placement='top' arrow>
                                  <MoreVertIcon
                                    sx={{
                                      fontSize: isMobile ? 16 : 20, // Increased icon size
                                      color: isEditModalOpen ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
                                    }}
                                  />
                                </StyledTooltip>
                              </ActionIconButton>
                            </>
                          )}
                        </Box>
                      )}
                    </StyledTableCell>
                  )}

                  {/* Add 'More' button for view mode when custom actions are present */}
                  {viewMode && !editMode && customActions && customActions.length > 0 && (
                    <StyledTableCell
                      className='actions-cell'
                      sx={{
                        background: '#E7EFF5 !important',
                        width: isMobile ? '50px !important' : '60px !important',
                        maxWidth: isMobile ? '50px !important' : '60px !important',
                        minWidth: isMobile ? '50px !important' : '60px !important',
                        padding: '0 4px !important',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowContextMenu(row, e as React.MouseEvent);
                          }}
                          disabled={isEditModalOpen}
                          sx={{
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <MoreVertIcon
                            sx={{
                              fontSize: isMobile ? 14 : 16,
                              color: isEditModalOpen ? 'rgba(0, 0, 0, 0.26)' : 'inherit',
                            }}
                          />
                        </IconButton>
                      </Box>
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </StyledTable>
        
        {/* Loading indicator is now centralized for all loading states */}
      </StyledTableContainer>

      {/* Show pagination controls only if not in lazy loading mode */}
      {!isTableLoading && showPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0,
          }}
          data-testid="pagination-controls"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '16px' : '24px',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <ShowAllButton onClick={handleShowAll} data-testid="show-all-button">
              {t(
                'table.showAllPendingActions',
                isMobile
                  ? `All ${totalCount || data.length}`
                  : `Show All ${totalCount || data.length}`
              )}
            </ShowAllButton>
            {todoColumn && (
            <Typography
              sx={{
                fontSize: '12px',
                color: '#666666',
                fontStyle: 'italic',
                fontFamily: "'Lato', sans-serif",
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {t(
                'table.status',
                '(*) Star icon indicates pending status: Gold star = Pending'
              )}
            </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '12px' : '16px',
              width: isMobile ? '100%' : 'auto',
              mt: isMobile ? 2 : 0,
            }}
          >
            {!isMobile && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'rgba(0, 0, 0, 0.8)',
                  fontFamily: "'Lato', sans-serif",
                  fontWeight: 500,
                }}
              >
                {t('table.itemsPerPage', 'Items per page')}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: isMobile ? 'space-between' : 'flex-start',
              }}
            >
              {isMobile && (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.8)',
                    fontFamily: "'Lato', sans-serif",
                    fontWeight: 500,
                  }}
                >
                  {t('table.itemsPerPage', 'Items:')}
                </Typography>
              )}

              <PaginationButton
                endIcon={<KeyboardArrowDownIcon />}
                onClick={(e) => setPageSizeAnchorEl(e.currentTarget)}
                size={isMobile ? 'small' : 'medium'}
                data-testid="page-size-button"
              >
                {pageSize}
              </PaginationButton>

              <PaginationButton 
                size={isMobile ? 'small' : 'medium'} 
                data-testid="page-navigation"
                endIcon={totalPages > 1 ? <KeyboardArrowDownIcon /> : undefined}
                onClick={totalPages > 1 ? (e) => setPageJumpAnchorEl(e.currentTarget) : undefined}
                sx={{
                  cursor: totalPages > 1 ? 'pointer' : 'default',
                  '&:hover': totalPages > 1 ? {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  } : {}
                }}
              >
                <KeyboardArrowLeftIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePageChange(currentPage - 1);
                  }}
                  sx={{ 
                    opacity: currentPage === 0 ? 0.5 : 1, 
                    fontSize: isMobile ? 16 : 24,
                    pointerEvents: 'auto',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: '4px',
                    }
                  }}
                  data-testid="previous-page-button"
                />
                <Box sx={{ 
                  px: 1, 
                  minWidth: isMobile ? '30px' : '40px',
                  textAlign: 'center',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: 500
                }}>
                  {currentPage + 1}
                </Box>
                <KeyboardArrowRightIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePageChange(currentPage + 1);
                  }}
                  sx={{
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    fontSize: isMobile ? 16 : 24,
                    pointerEvents: 'auto',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: '4px',
                    }
                  }}
                  data-testid="next-page-button"
                />
              </PaginationButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* In lazy loading mode, show a simple data count and loaded percentage */}
      {!isTableLoading && showPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
          }}
        >
          {hasMore && (
            <Button 
              variant="text" 
              size="small"
              disabled={isTableLoading}
              onClick={() => onLoadMore && onLoadMore()}
              data-testid="load-more-button"
            >
              {t('table.loadMore', 'Load More')}
            </Button>
          )}
        </Box>
      )}

      <Menu
        anchorEl={pageSizeAnchorEl}
        open={Boolean(pageSizeAnchorEl)}
        onClose={() => setPageSizeAnchorEl(null)}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? '100px' : 'auto',
          },
        }}
        data-testid="page-size-menu"
      >
        {pageSizeOptions.map((size) => (
          <MenuItem
            key={size}
            onClick={() => handlePageSizeChange(size)}
            selected={size === pageSize}
            dense={isMobile}
          >
            {size}
          </MenuItem>
        ))}
      </Menu>

      {/* Page Jump Menu */}
      <Menu
        anchorEl={pageJumpAnchorEl}
        open={Boolean(pageJumpAnchorEl)}
        onClose={() => setPageJumpAnchorEl(null)}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: isMobile ? '140px' : '180px',
          },
        }}
        data-testid="page-jump-menu"
      >
        
        {Array.from({ length: totalPages }, (_, pageIndex) => (
          <MenuItem
            key={pageIndex}
            onClick={() => handlePageJump(pageIndex)}
            selected={pageIndex === currentPage}
            dense={isMobile}
            sx={{
              justifyContent: 'center',
              fontWeight: pageIndex === currentPage ? 600 : 400,
              fontSize: isMobile ? '12px' : '13px',
              py: isMobile ? 0.5 : 1,
            }}
          >
           {pageIndex + 1}
          </MenuItem>
        ))}
      </Menu>

      <SortMenu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableAutoFocusItem
        data-testid="sort-menu"
      >
        {enableSearch && (
          <>
            {/* Search Input Section */}
            <Box sx={{ p: 2, minWidth: 250 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '11px', 
                  mb: 1, 
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: 500,
                }}
              >
                {t('table.searchInColumn', `Search in "${activeColumn}" column`)}
                {isSearchActive && currentSearchColumn === activeColumn && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontSize: '10px', 
                      color: '#2196F3',
                      fontWeight: 600,
                      ml: 1,
                    }}
                  >
                    ({t('table.searchActive', 'Active')}: "{currentSearchTerm}")
                  </Typography>
                )}
              </Typography>
              <TextField
                size="small"
                placeholder={
                  currentSearchColumn === activeColumn && currentSearchTerm
                    ? t('table.searchPlaceholderWithTerm', `Current: "${currentSearchTerm}" - Enter new term...`)
                    : t('table.searchPlaceholder', `Enter search term...`)
                }
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                onKeyDown={handleSearchKeyDown}
                autoFocus
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ 
                        fontSize: 18, 
                        color: currentSearchColumn === activeColumn ? '#2196F3' : 'rgba(0, 0, 0, 0.54)' 
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: (searchTerm || (activeColumn && currentFilters.some(f => f.column === activeColumn))) && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ p: 0.5 }}
                        title={t('table.clearSearch', 'Clear search')}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '12px',
                    border: currentSearchColumn === activeColumn ? '1px solid #2196F3' : 'none',
                    '& .MuiInputBase-input': {
                      fontSize: '12px',
                      py: 1,
                    },
                  },
                }}
                data-testid="search-input"
              />
              
              {/* Helper text */}
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '10px', 
                  mt: 1, 
                  display: 'block',
                  color: currentSearchColumn === activeColumn ? '#2196F3' : 'rgba(0, 0, 0, 0.5)',
                  fontStyle: 'italic',
                }}
              >
                {searchTerm.trim() 
                  ? t('table.searchHint', 'Use sort options below to search with this term')
                  : t('table.searchOrSort', 'Type to search or use sort options below')
                }
              </Typography>
            </Box>
            <Divider />
          </>
        )}
        
        {/* Sort Options - now handle both search and regular sort */}
        <SortMenuItem 
          onClick={() => handleSortSelect('ASC')} 
          data-testid="sort-ascending"
          sx={{
            backgroundColor: enableSearch && searchTerm.trim() ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
            '&:hover': {
              backgroundColor: enableSearch && searchTerm.trim() ? 'rgba(33, 150, 243, 0.08)' : '#f5f5f5',
            }
          }}
        >
          <ArrowUpwardIcon sx={{ color: enableSearch && searchTerm.trim() ? '#2196F3' : 'inherit' }} />
          {enableSearch && searchTerm.trim() 
            ? t('table.searchSortAsc', 'Search & Sort Ascending')
            : t('table.sortAscending', 'Sort Ascending')
          }
          {enableSearch && searchTerm.trim() && (
            <SearchIcon sx={{ fontSize: 14, ml: 'auto', color: '#2196F3' }} />
          )}
        </SortMenuItem>
        <SortMenuItem 
          onClick={() => handleSortSelect('DESC')} 
          data-testid="sort-descending"
          sx={{
            backgroundColor: enableSearch && searchTerm.trim() ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
            '&:hover': {
              backgroundColor: enableSearch && searchTerm.trim() ? 'rgba(33, 150, 243, 0.08)' : '#f5f5f5',
            }
          }}
        >
          <ArrowDownwardIcon sx={{ color: enableSearch && searchTerm.trim() ? '#2196F3' : 'inherit' }} />
          {enableSearch && searchTerm.trim() 
            ? t('table.searchSortDesc', 'Search & Sort Descending') 
            : t('table.sortDescending', 'Sort Descending')
          }
          {enableSearch && searchTerm.trim() && (
            <SearchIcon sx={{ fontSize: 14, ml: 'auto', color: '#2196F3' }} />
          )}
        </SortMenuItem>
        
        {/* Clear Sort Option - only show if there's an active sort on this column */}
        {currentSort && currentSort.column === activeColumn && (
          <>
            <Divider />
            <SortMenuItem 
              onClick={() => handleSortSelect(currentSort.direction)} 
              data-testid="clear-sort"
              sx={{
                backgroundColor: 'rgba(244, 67, 54, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                }
              }}
            >
              <CloseIcon sx={{ color: '#F44336', fontSize: 16 }} />
              {t('table.clearSort', 'Clear Sort')}
            </SortMenuItem>
          </>
        )}
        
        {/* Clear All Filters Option - only show if both search and sort are active */}
        {enableSearch && isSearchActive && currentSort && currentSort.column === activeColumn && (
          <>
            <Divider />
            <SortMenuItem 
              onClick={() => {
                setSearchTerm('');
                setIsSearchActive(false);
                setCurrentSearchColumn(null);
                setCurrentSearchTerm('');
                setCurrentSort(undefined);
                if (onSort) {
                  onSort(activeColumn, undefined as any);
                }
                setSortAnchorEl(null);
              }} 
              data-testid="clear-all-filters"
              sx={{
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.12)',
                }
              }}
            >
              <ClearIcon sx={{ color: '#F44336', fontSize: 16 }} />
              {t('table.clearAllFilters', 'Clear All Filters')}
            </SortMenuItem>
          </>
        )}
      </SortMenu>

      {/* Context Menu for edit mode or regular mode */}
      {contextMenu !== null && (
        <TableContextMenu
          open={true}
          onClose={handleCloseContextMenu}
          onAction={handleContextMenuAction}
          anchorPosition={{
            top: contextMenu.mouseY,
            left: contextMenu.mouseX,
          }}
          currentRow={contextMenu.row}
          editModeOnly={editMode}
          viewMode={viewMode}
          customActions={customActions}
          hideDefaultActions={hideDefaultActions || viewMode}
          data-testid="table-context-menu"
        />
      )}

      {/* Edit Modal */}
      {editMode && (
        <EditModal
          isOpen={isEditModalOpen}
          onClose={handleCancelEdit}
          onSave={handleSaveRow}
          row={selectedRow}
          columns={columns}
          readOnlyColumns={readOnlyColumns}
          excludedColumns={excludedColumns}
          requiredFields={requiredFields}
          textAreaColumns={textAreaColumns}
          dateColumns={dateColumns}
          priorityReadOnlyColumns={priorityReadOnlyColumns}
          defaultReadOnlyColumns={defaultReadOnlyColumns}
          title={t('table.editRow', 'Edit Row')}
          data-testid="edit-row-modal"
        />
      )}

      {/* Add Snackbar for notifications */}
      {notification && (
        <Snackbar 
          open={notification.open} 
          autoHideDuration={6000} 
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            top: '20% !important',
            right: 'auto',
            maxWidth: '90%',
            transform: 'translate(-50%, -50%) !important',
          }}
          data-testid="table-notification"
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.type} 
            sx={{ 
              width: '100%',
              minWidth: { xs: '250px', sm: '300px' },
              boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </TableWrapper>
  );
};

export default Table;
